import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  type AddMediaToProjectsInput,
  type MediaProjectAssignment,
  type ProjectMediaItem,
  type ProjectRow,
  projectAssignmentService,
} from '@/services/data/ProjectAssignmentService';

export function useAssignableProjects(enabled = true) {
  return useQuery<ProjectRow[]>({
    queryKey: ['projects', 'assignable'],
    enabled,
    queryFn: async () => {
      const result = await projectAssignmentService.listAssignableProjects();
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    staleTime: 30_000,
  });
}

export function usePostProjectContexts(projectIds: string[], enabled = true) {
  const normalized = [...new Set(projectIds)].sort();
  return useQuery<ProjectRow[]>({
    queryKey: ['projects', 'post-contexts', normalized],
    enabled: enabled && normalized.length > 0,
    queryFn: async () => {
      const result = await projectAssignmentService.listProjectsByIds(normalized);
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    staleTime: 30_000,
  });
}

export function useMediaProjectAssignments(mediaIds: string[], enabled = true) {
  const normalized = [...new Set(mediaIds)].sort();
  return useQuery<MediaProjectAssignment[]>({
    queryKey: ['projectAssignments', 'media', normalized],
    enabled: enabled && normalized.length > 0,
    queryFn: async () => {
      const result = await projectAssignmentService.listMediaProjectAssignments(normalized);
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    staleTime: 10_000,
  });
}

export function useAddMediaToProjects() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddMediaToProjectsInput) => {
      const result = await projectAssignmentService.addMediaToProjects(input);
      if (result.error) throw new Error(result.error.message);
      return result.data ?? { inserted: 0, projectNames: [] };
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projectAssignments', 'media'] }),
        queryClient.invalidateQueries({ queryKey: ['projectFiles'] }),
        queryClient.invalidateQueries({ queryKey: ['projectMediaItems'] }),
        queryClient.invalidateQueries({ queryKey: ['library'] }),
        ...variables.projectIds.map((projectId) =>
          queryClient.invalidateQueries({ queryKey: ['projectMediaItems', projectId] }),
        ),
      ]);
    },
  });
}

export function useProjectMediaItems(projectId: string | null | undefined, enabled = true) {
  return useQuery<ProjectMediaItem[]>({
    queryKey: ['projectMediaItems', projectId ?? null],
    enabled: enabled && Boolean(projectId),
    queryFn: async () => {
      if (!projectId) return [];
      const result = await projectAssignmentService.listProjectMedia(projectId);
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    staleTime: 10_000,
  });
}

export function useToggleProjectMediaFavorite(projectId: string | null | undefined) {
  const queryClient = useQueryClient();
  const key = ['projectMediaItems', projectId ?? null];
  return useMutation({
    mutationFn: async ({ mediaId, favorited }: { mediaId: string; favorited: boolean }) => {
      if (!projectId) throw new Error('No project');
      const result = await projectAssignmentService.setProjectMediaFavorite({
        projectId,
        mediaId,
        favorited,
      });
      if (result.error) throw new Error(result.error.message);
    },
    onMutate: async ({ mediaId, favorited }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ProjectMediaItem[]>(key);
      queryClient.setQueryData<ProjectMediaItem[]>(key, (items = []) =>
        items.map((item) =>
          item.media.id === mediaId
            ? { ...item, favoritedAt: favorited ? new Date().toISOString() : null }
            : item,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
      toast.error('Could not update favorite');
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key, refetchType: 'none' }),
  });
}
